import { Box } from "@mui/material";
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route } from "react-router-dom";
import AppLayout from "../components/layout/AppLayout";
import SimplifiedAppLayout from "../components/layout/SimplifiedAppLayout";
import { PageType } from "../config/page.types";
import { BreadcrumbProvider } from "../context/BreadcrumbsProvider";
import { FiltersProvider } from "../context/FiltersProvider";
import { PageConfigurationProvider } from "../context/PageConfigrations/PageConfigurationProvider";
import CarbonSnapComposer from "../features/additionalservices/CarbonSnap/CarbonSnapComposer";
import CarbonSnapResults from "../features/additionalservices/CarbonSnap/CarbonSnapResults";
import SettingsAdmin from "../features/admin/SettingsAdmin";
import CurrentUser from "../features/auth/CurrentUser";
import Protected from "../features/auth/Protected";
import ResetPassword from "../features/auth/ResetPassword";
import SignIn from "../features/auth/SignIn";
import { Configuration } from "../features/dataCollection/Configuration";
import { ConfigurationOperationalUnits } from "../features/dataCollection/Configuration/ConfigurationOperationalUnits";
import { ConfigurationDetail } from "../features/dataCollection/Configuration/ConfigurationOperationalUnits/ConfigurationDetail";
import { Import } from "../features/dataCollection/Import";
import PlaceholderView from "../features/PlaceholderView";
import ResultsDashboardInsights from "../features/results/ResultsDashboardInsights";
import ResultsDashboardInsightsDetail from "../features/results/ResultsDashboardInsights/ResultsDashboardInsightsDetail";
import OperationalUnits from "../features/results/ResultsOperationalUnits";
import OperationalUnitsGreenrouter from "../features/results/ResultsOperationalUnits/OperationalUnitsGreenrouter";
import OperationalUnitsGreenrouterDetail from "../features/results/ResultsOperationalUnits/OperationalUnitsGreenrouter/OperationalUnitsGreenrouterDetail";
import OperationalUnitsThirdParty from "../features/results/ResultsOperationalUnits/OperationalUnitsThirdParty";
import OperationalUnitsThirdPartyDetail from "../features/results/ResultsOperationalUnits/OperationalUnitsThirdParty/OperationalUnitsThirdPartyDetail";
import ResultsOverview from "../features/results/ResultsOverview";
import ResultsTransport from "../features/results/ResultsTransport";
import TransportGreenrouter from "../features/results/ResultsTransport/TransportGreenrouter";
import EntityDetails from "../features/results/ResultsTransport/TransportGreenrouter/EntityDetails";
import TransportGreenrouterDetail from "../features/results/ResultsTransport/TransportGreenrouter/TransportGreenrouterDetail";
import TransportThirdParty from "../features/results/ResultsTransport/TransportThirdParty";
import TransportThirdPartyDetail from "../features/results/ResultsTransport/TransportThirdParty/TransportThirdPartyDetail";
import ErrorView from "../features/service/ErrorView";
import { RootCause } from "../features/whatIf/RootCause";
import { RootCauseResults } from "../features/whatIf/RootCause/RootCauseResults";
import { BackToView, Http } from "./http";
import { AppRoutes } from "./routesUtils";

const homeRoute = createRoutesFromElements(
	<Route index id="home" path={AppRoutes.Home} element={<Navigate to={AppRoutes.PostSignInHome} />} />
);

// TODO: evaluate implementation of error boundaries (see.: https://github.com/bvaughn/react-error-boundary)
// Error boundaries may be used in conjunction with error pages for better error management.

const publicRoutes = createRoutesFromElements(
	<Route
		id="public-routes"
		element={
			<Box sx={{ display: "flex" }}>
				<SimplifiedAppLayout />
				<Box
					component="main"
					sx={{
						flex: 1,
						minWidth: 0, // flex 1 need this property to be able to resize in smaller size
						maxWidth: "100%",
						overflowX: "hidden",
					}}
				>
					<Outlet />
				</Box>
			</Box>
		}
	>
		{/* Sign in, unauthorized error */}
		<Route id="sign-in" path={AppRoutes.SignIn} element={<SignIn />} />
		<Route id="recover-password" path={AppRoutes.ResetPassword} element={<ResetPassword />} />
		<Route
			id="unauthorized"
			path={AppRoutes.Unauthorized}
			element={<ErrorView httpStatusCode={Http.Unauthorized} backToSection={BackToView.SignIn} />}
		/>

		{/* Not found error */}
		<Route
			id="not-found"
			path="*"
			element={<ErrorView httpStatusCode={Http.NotFound} backToSection={BackToView.Home} />}
		/>

		{/* Forbidden error */}
		<Route
			id="forbidden"
			path={AppRoutes.Forbidden}
			element={<ErrorView httpStatusCode={Http.Forbidden} backToSection={BackToView.Home} />}
		/>
	</Route>
);

const protectedRoutes = createRoutesFromElements(
	<Route
		id="protected-routes"
		element={
			<PageConfigurationProvider>
				<FiltersProvider>
					<BreadcrumbProvider>
						<Box sx={{ display: "flex" }}>
							<AppLayout />
							<Box
								component="main"
								sx={{
									flex: 1,
									minWidth: 0, // flex 1 need this property to be able to resize in smaller size
									maxWidth: "100%",
									overflowX: "hidden",
								}}
							>
								<Outlet />
							</Box>
						</Box>
					</BreadcrumbProvider>
				</FiltersProvider>
			</PageConfigurationProvider>
		}
	>
		{/* Account */}
		<Route
			path={AppRoutes.Account}
			element={
				<Protected>
					<CurrentUser />
				</Protected>
			}
		/>

		{/* Home */}
		<Route
			path={AppRoutes.PostSignInHome}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.Home} text="Home" />
				</Protected>
			}
		/>

		{/* Data collection */}
		<Route path={AppRoutes.DataCollection} element={<Navigate to={AppRoutes.DataCollectionMonitor} />} />

		<Route
			path={AppRoutes.DataCollectionMonitor}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.DataCollectionMonitor} text="Monitor" />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataCollectionImport}
			element={
				<Protected>
					<Import />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataCollectionConfiguration}
			element={
				<Protected>
					<Configuration />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataCollectionConfigurationOperationalUnits}
			element={
				<Protected>
					<ConfigurationOperationalUnits />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.DataCollectionConfigurationOperationalUnitsDetail}
			element={
				<Protected>
					<ConfigurationDetail />
				</Protected>
			}
		/>

		{/* Results */}
		<Route path={AppRoutes.Results} element={<Navigate to={AppRoutes.ResultsOverview} />} />

		<Route
			path={AppRoutes.ResultsOverview}
			element={
				<Protected>
					<ResultsOverview />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsDetails}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.ResultsDetails} text="Details" />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsReport}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.ResultsReport} text="Report" />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsDashboardInsights}
			element={
				<Protected>
					<ResultsDashboardInsights />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsDashboardInsightsDetail}
			element={
				<Protected>
					<ResultsDashboardInsightsDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransport}
			element={
				<Protected>
					<ResultsTransport />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportGreenrouter}
			element={
				<Protected>
					<TransportGreenrouter />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportGreenrouterDetail}
			element={
				<Protected>
					<TransportGreenrouterDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsEntityGreenRouterDetail}
			element={
				<Protected>
					<EntityDetails />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportThirdParty}
			element={
				<Protected>
					<TransportThirdParty />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsTransportThirdPartyDetail}
			element={
				<Protected>
					<TransportThirdPartyDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnits}
			element={
				<Protected>
					<OperationalUnits />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnitsGreenRouter}
			element={
				<Protected>
					<OperationalUnitsGreenrouter />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultOperationalUnitsGreerouterNode}
			element={
				<Protected>
					<OperationalUnitsGreenrouterDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnitsThirdParty}
			element={
				<Protected>
					<OperationalUnitsThirdParty />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.ResultsOperationalUnitsThirdPartyDetail}
			element={
				<Protected>
					<OperationalUnitsThirdPartyDetail />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.WhatIfCarbonBudget}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.WhatIfCarbonBudget} text="Carbon Budget" />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.WhatIfRootCause}
			element={
				<Protected>
					<RootCause />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.WhatIfRootCauseResults}
			element={
				<Protected>
					<RootCauseResults />
				</Protected>
			}
		/>

		{/* Additional Services */}
		<Route path={AppRoutes.AdditionalServices} element={<Navigate to={AppRoutes.AdditionalServicesCarbonSnap} />} />

		<Route
			path={AppRoutes.AdditionalServicesCarbonSnap}
			element={
				<Protected>
					<CarbonSnapComposer />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.AdditionalServicesCarbonSnapResults}
			element={
				<Protected>
					<CarbonSnapResults />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.AdditionalServicesDocumentation}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.AdditionalServicesDocumentation} text="Documentation" />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.AdditionalServicesTourWizard}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.AdditionalServicesTourWizard} text="Tour, Wizard" />
				</Protected>
			}
		/>

		{/* Settings */}
		<Route path={AppRoutes.Settings} element={<Navigate to={AppRoutes.SettingsAdmin} />} />

		<Route
			path={AppRoutes.SettingsAdmin}
			element={
				<Protected>
					<SettingsAdmin />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.SettingsAuthApi}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.SettingsAuthApi} text="Auth, API" />
				</Protected>
			}
		/>

		<Route
			path={AppRoutes.SettingsPersonalised}
			element={
				<Protected>
					<PlaceholderView pageType={PageType.SettingsPersonalised} text="Customisation" />
				</Protected>
			}
		/>
	</Route>
);

/**
 * Exports registered (active) application routes.
 */
export const applicationRoutes = createBrowserRouter([...homeRoute, ...publicRoutes, ...protectedRoutes]);
